/**
 * PosCustomerDisplay — fullscreen second-screen that mirrors the
 * cashier's live cart. Polls /api/pos/cart-mirror/{session_id} every 1s.
 *
 * Route: /pos/display/:sessionId  (PUBLIC — no auth gating; relies on the
 *                                  random session_id as a soft secret).
 *
 * States rendered:
 *   • cart        → live items + running total
 *   • tip_prompt  → "Add a tip?" with 10/15/20% + No tip buttons (Stripe-style)
 *   • tipped      → Thank-you + total-with-tip
 *   • paid        → simple "Payment received — thank you!" card
 *
 * Inspired by itpp-labs/pos-addons "POS Customer Display" + OCA
 * "POS Customer Screen" + Square / Stripe Terminal tip prompts.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PosCustomerDisplay = () => {
  const { sessionId } = useParams();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data } = useQuery({
    queryKey: ['cart-mirror', sessionId],
    queryFn: async () => (await axios.get(
      `${API}/api/pos/cart-mirror/${encodeURIComponent(sessionId)}`
    )).data,
    refetchInterval: 1000,
  });

  const state = data?.state || 'cart';
  const items = data?.items || [];
  const subtotal = Number(data?.subtotal || 0);
  const currency = data?.currency || 'USD';
  const suggested = data?.tip_suggested_pct || [10, 15, 20];
  const allowCustom = data?.tip_allow_custom !== false;
  const tipAmount = Number(data?.tip_amount || 0);

  const applyTip = useMutation({
    mutationFn: async ({ amount, pct }) => {
      setSubmitting(true);
      await axios.post(
        `${API}/api/pos/cart-mirror/${encodeURIComponent(sessionId)}/apply-tip`,
        { amount: Number(amount.toFixed(2)), pct: pct ?? null }
      );
    },
    onSettled: () => { setSubmitting(false); qc.invalidateQueries({ queryKey: ['cart-mirror', sessionId] }); },
  });

  const fmt = (n) => `${currency} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ============== Tip Prompt screen ==============
  if (state === 'tip_prompt') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-slate-900 to-fuchsia-900 text-white p-10 flex flex-col items-center justify-center"
           data-testid="pos-customer-display-tip">
        <div className="text-xs uppercase tracking-[0.4em] text-indigo-200 mb-8">
          Faith World Ministries · POS
        </div>
        <div className="text-5xl font-light mb-3 text-center">Add a tip?</div>
        <div className="text-lg text-slate-300 mb-12 text-center">
          Subtotal {fmt(subtotal)} · 100% supports our team
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 max-w-3xl w-full mb-6">
          {suggested.map((pct) => {
            const amt = subtotal * (pct / 100);
            return (
              <button
                key={pct}
                disabled={submitting}
                onClick={() => applyTip.mutate({ amount: amt, pct })}
                className="aspect-square rounded-3xl bg-white/10 hover:bg-white/20 border border-white/20
                            transition flex flex-col items-center justify-center gap-2 disabled:opacity-40"
                data-testid={`tip-pct-${pct}`}
              >
                <div className="text-7xl font-bold">{pct}%</div>
                <div className="text-2xl text-indigo-200 tabular-nums">{fmt(amt)}</div>
              </button>
            );
          })}
        </div>

        <div className="flex gap-4 mt-2">
          <button
            disabled={submitting}
            onClick={() => applyTip.mutate({ amount: 0, pct: 0 })}
            className="px-8 py-4 rounded-full border border-white/25 hover:bg-white/10 transition disabled:opacity-40"
            data-testid="tip-none"
          >
            No tip · {fmt(0)}
          </button>
          {allowCustom && (
            <button
              disabled={submitting}
              onClick={() => {
                const raw = window.prompt(`Custom tip in ${currency}`);
                const amt = parseFloat(raw);
                if (!Number.isNaN(amt) && amt >= 0) applyTip.mutate({ amount: amt });
              }}
              className="px-8 py-4 rounded-full border border-white/25 hover:bg-white/10 transition disabled:opacity-40"
              data-testid="tip-custom"
            >
              Custom…
            </button>
          )}
        </div>

        <div className="text-[10px] text-slate-500 mt-10 font-mono">{sessionId}</div>
      </div>
    );
  }

  // ============== Tipped / Paid thank-you screen ==============
  if (state === 'tipped' || state === 'paid') {
    return (
      <div className="min-h-screen bg-emerald-950 text-white flex flex-col items-center justify-center p-10"
           data-testid="pos-customer-display-thanks">
        <div className="text-emerald-300 text-7xl mb-6">♥</div>
        <div className="text-5xl font-light mb-3">Thank you!</div>
        {state === 'tipped' && tipAmount > 0 && (
          <div className="text-xl text-emerald-200">
            Tip {fmt(tipAmount)} · Total {fmt(subtotal + tipAmount)}
          </div>
        )}
        {state === 'tipped' && tipAmount === 0 && (
          <div className="text-xl text-emerald-200">Total {fmt(subtotal)}</div>
        )}
        <div className="text-xs text-emerald-500 mt-10 font-mono">{sessionId}</div>
      </div>
    );
  }

  // ============== Default "cart" screen ==============
  return (
    <div className="min-h-screen bg-black text-white p-10 flex flex-col"
         data-testid="pos-customer-display">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-slate-500">
            Faith World Ministries · POS
          </div>
          {data?.cashier_name && (
            <div className="text-sm text-slate-400 mt-1">Cashier · {data.cashier_name}</div>
          )}
        </div>
        <div className="text-xs text-slate-600 font-mono">{sessionId}</div>
      </header>

      <div className="flex-1 grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-2 overflow-y-auto">
          {items.length === 0 && (
            <div className="h-full flex items-center justify-center text-center text-slate-600">
              <div>
                <div className="text-6xl mb-4">🛒</div>
                <div className="text-2xl">Waiting for cashier…</div>
              </div>
            </div>
          )}
          {items.map((it, idx) => (
            <div key={idx} className="flex items-center justify-between border-b border-slate-800 py-3">
              <div className="flex items-center gap-4">
                <div className="text-3xl text-slate-500 tabular-nums w-12 text-right">
                  {it.qty}×
                </div>
                <div className="text-3xl text-white">{it.name}</div>
              </div>
              <div className="text-2xl text-slate-300 tabular-nums">
                {fmt(Number(it.price) * Number(it.qty))}
              </div>
            </div>
          ))}
        </div>
        <div className="col-span-4">
          <div className="bg-gradient-to-br from-indigo-500/15 to-fuchsia-500/5 border border-indigo-400/30 rounded-2xl p-8 sticky top-8">
            <div className="text-sm uppercase tracking-[0.3em] text-indigo-200">
              Total
            </div>
            <div className="text-7xl font-semibold text-white mt-3 tabular-nums">
              {fmt(subtotal)}
            </div>
            <div className="mt-6 text-sm text-slate-400">
              {items.reduce((s, x) => s + Number(x.qty || 0), 0)} items
            </div>
            {data?.note && (
              <div className="mt-6 text-sm text-slate-300 bg-slate-900/60 border border-slate-700 rounded-md p-3">
                {data.note}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="text-center text-xs text-slate-700 pt-6">
        Thank you for supporting Faith World Ministries
      </footer>
    </div>
  );
};

export default PosCustomerDisplay;
